import React, { useState } from 'react';
import { ArrowLeft, Upload, Check, User, Home, Palette, MessageSquare, Camera, Award } from 'lucide-react';
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics';

interface OrderFormProps {
  onBack: () => void;
}

interface FormData {
  // Customer Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  
  // House Details
  houseStyle: string;
  yearBuilt: string;
  specialFeatures: string;
  preferredAngle: string;
  seasonPreference: string;
  
  // Pillow Specifications
  pillowSize: string;
  quantity: number;
  fabricPreference: string;
  colorScheme: string;
  
  // Design Details
  designDescription: string;
  specialInstructions: string;
  hasReference: boolean;
  referenceFiles: File[];
  
  // Timeline & Budget
  timeline: string;
  budget: string;
  isGift: boolean;
  giftOccasion: string;
}

export default function OrderForm({ onBack }: OrderFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const { trackEvent } = useGoogleAnalytics();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    houseStyle: '',
    yearBuilt: '',
    specialFeatures: '',
    preferredAngle: '',
    seasonPreference: '',
    pillowSize: '',
    quantity: 1,
    fabricPreference: '',
    colorScheme: '',
    designDescription: '',
    specialInstructions: '',
    hasReference: false,
    referenceFiles: [],
    timeline: '',
    budget: '',
    isGift: false,
    giftOccasion: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (files: FileList | null) => {
    if (files) {
      setFormData(prev => ({
        ...prev,
        referenceFiles: [...prev.referenceFiles, ...Array.from(files)]
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Track form submission attempt
    trackEvent('form_submit', {
      form_name: 'commission_order',
      step: currentStep,
      pillow_size: formData.pillowSize,
      quantity: formData.quantity,
      timeline: formData.timeline,
      is_gift: formData.isGift
    });

    try {
      // For static site: Log form data to console
      console.log('Commission form submitted:', formData);
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      setSubmitted(true);
      
      // Track successful form submission
      trackEvent('form_submit_success', {
        form_name: 'commission_order',
        pillow_size: formData.pillowSize,
        quantity: formData.quantity,
        timeline: formData.timeline,
        is_gift: formData.isGift
      });
    } catch (error) {
      console.error('Error submitting commission:', error);
      console.log('There was an error submitting your commission. Please try again.');
      
      // Track form submission error
      trackEvent('form_submit_error', {
        form_name: 'commission_order',
        error_message: 'Submission failed'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Contact Info', icon: User },
    { number: 2, title: 'Your Home', icon: Home },
    { number: 3, title: 'Design Vision', icon: Palette },
    { number: 4, title: 'Final Details', icon: MessageSquare }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-3xl w-full text-center">
          <div className="bg-emerald-100 w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="h-14 w-14 text-emerald-600" />
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Commission Submitted Successfully!</h2>
          <p className="text-xl text-slate-600 mb-10 leading-relaxed">
            Thank you for choosing Mimiiams for your custom house portrait pillow! We're excited to begin creating your unique piece of art. You'll receive a confirmation email shortly with next steps.
          </p>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 mb-10 text-left">
            <div className="flex items-center mb-4">
              <Award className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="font-bold text-blue-900 text-lg">What Happens Next?</h3>
            </div>
            <ul className="text-blue-800 space-y-3">
              <li className="flex items-start">
                <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                <span>Mimi will review your photos and design requirements within 24 hours from her Chicago North Shore studio</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                <span>Mimi will create a detailed design sketch for your approval</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                <span>You'll receive a detailed quote and timeline (typically 4-6 weeks)</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                <span>Upon approval, you'll receive a secure payment link</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</span>
                <span>Mimi begins hand-embroidering your masterpiece in her Chicago studio</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 font-medium">
              <strong>Important:</strong> Please ensure your reference photos are high-resolution and show your home clearly. 
              If you need guidance on the best photos to submit, Mimi will be in touch from her Chicago North Shore studio!
            </p>
          </div>

          <button
            onClick={onBack}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg"
          >
            Return to Home
          </button>
          
          <div className="mt-8 text-center text-slate-600 text-sm">
            <p><strong>Mimi Iams Fabric Art</strong> • Chicago North Shore, Illinois • (847) 251-4490</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-3 text-slate-600 hover:text-emerald-600 transition-colors group"
            >
              <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Home</span>
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-900">Custom House Portrait Commission</h1>
              <p className="text-slate-600 mt-1">Mimi Iams Fabric Art • Chicago North Shore</p>
            </div>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-16">
          <div className="flex justify-between items-center relative">
            <div className="absolute top-6 left-0 right-0 h-1 bg-stone-200 -z-10"></div>
            <div 
              className="absolute top-6 left-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 -z-10 transition-all duration-500"
              style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
            ></div>
            
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex flex-col items-center relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                    isCompleted ? 'bg-emerald-500 text-white shadow-lg' : 
                    isActive ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg scale-110' : 
                    'bg-white border-2 border-stone-300 text-stone-400'
                  }`}>
                    {isCompleted ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                  </div>
                  <span className={`text-sm font-semibold text-center ${
                    isActive ? 'text-emerald-600' : isCompleted ? 'text-emerald-600' : 'text-stone-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-10">
          {/* Step 1: Contact Information */}
          {currentStep === 1 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Let's Get Started</h2>
                <p className="text-slate-600 text-lg">We'll need your contact information to keep you updated on your commission</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Street Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="Your city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Zip Code</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: House Details */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Tell Us About Your Home</h2>
                <p className="text-slate-600 text-lg">Help us understand your home's unique character and architectural style</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Architectural Style *</label>
                  <select
                    required
                    value={formData.houseStyle}
                    onChange={(e) => handleInputChange('houseStyle', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select your home's style</option>
                    <option value="colonial">Colonial</option>
                    <option value="tudor">Tudor Revival</option>
                    <option value="victorian">Victorian</option>
                    <option value="craftsman">Craftsman</option>
                    <option value="georgian">Georgian</option>
                    <option value="mediterranean">Mediterranean</option>
                    <option value="modern">Modern/Contemporary</option>
                    <option value="farmhouse">Farmhouse</option>
                    <option value="cape-cod">Cape Cod</option>
                    <option value="ranch">Ranch</option>
                    <option value="other">Other/Not Sure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Year Built (Approximate)</label>
                  <input
                    type="text"
                    value={formData.yearBuilt}
                    onChange={(e) => handleInputChange('yearBuilt', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="e.g., 1925, 1980s, 2010"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Special Architectural Features</label>
                <textarea
                  rows={3}
                  value={formData.specialFeatures}
                  onChange={(e) => handleInputChange('specialFeatures', e.target.value)}
                  placeholder="Describe unique features like dormers, columns, bay windows, stonework, distinctive rooflines, etc."
                  className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Preferred Viewing Angle *</label>
                  <select
                    required
                    value={formData.preferredAngle}
                    onChange={(e) => handleInputChange('preferredAngle', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select preferred angle</option>
                    <option value="front-straight">Front View (Straight On)</option>
                    <option value="front-angle">Front View (Angled)</option>
                    <option value="three-quarter">Three-Quarter View</option>
                    <option value="side">Side View</option>
                    <option value="discuss">Let's Discuss Options</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Season/Landscaping Preference</label>
                  <select
                    value={formData.seasonPreference}
                    onChange={(e) => handleInputChange('seasonPreference', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select preference</option>
                    <option value="spring">Spring (Blooming)</option>
                    <option value="summer">Summer (Full Foliage)</option>
                    <option value="fall">Fall (Autumn Colors)</option>
                    <option value="winter">Winter (Bare Trees)</option>
                    <option value="evergreen">Focus on Evergreens</option>
                    <option value="minimal">Minimal Landscaping</option>
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <div className="flex items-start">
                  <Camera className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Photo Tips for Best Results</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Take photos during golden hour (early morning or late afternoon) for best lighting</li>
                      <li>• Ensure the entire house is visible and in focus</li>
                      <li>• Include some landscaping and surrounding context</li>
                      <li>• Multiple angles are helpful - we'll choose the best one together</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Design Vision */}
          {currentStep === 3 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Your Design Vision</h2>
                <p className="text-slate-600 text-lg">Share your photos and tell us about your pillow preferences</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-4">Upload House Photos *</label>
                <div className="border-2 border-dashed border-stone-300 rounded-2xl p-8 text-center hover:border-emerald-400 transition-colors bg-gradient-to-br from-emerald-50 to-teal-50">
                  <Upload className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-slate-800 mb-2">Upload Your House Photos</h4>
                  <p className="text-slate-600 mb-2">High-resolution photos work best (JPG, PNG, HEIC)</p>
                  <p className="text-sm text-slate-500 mb-6">Multiple angles welcome - up to 10MB per file</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 cursor-pointer transition-all duration-300 shadow-lg"
                  >
                    Choose Photos
                  </label>
                  {formData.referenceFiles.length > 0 && (
                    <div className="mt-6 p-4 bg-emerald-100 rounded-xl">
                      <p className="text-emerald-800 font-semibold">
                        ✓ {formData.referenceFiles.length} photo(s) uploaded successfully
                      </p>
                      <p className="text-emerald-700 text-sm mt-1">
                        {formData.referenceFiles.map(file => file.name).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Pillow Size *</label>
                  <select
                    required
                    value={formData.pillowSize}
                    onChange={(e) => handleInputChange('pillowSize', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select pillow size</option>
                    <option value="12x16">12" x 16" (Lumbar)</option>
                    <option value="14x20">14" x 20" (Lumbar)</option>
                    <option value="16x16">16" x 16" (Square)</option>
                    <option value="18x18">18" x 18" (Square)</option>
                    <option value="20x20">20" x 20" (Square)</option>
                    <option value="custom">Custom Size</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Quantity</label>
                  <select
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', parseInt(e.target.value))}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value={1}>1 Pillow</option>
                    <option value={2}>2 Pillows (Matching Pair)</option>
                    <option value={3}>3 Pillows</option>
                    <option value={4}>4 Pillows</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Fabric Preference</label>
                  <select
                    value={formData.fabricPreference}
                    onChange={(e) => handleInputChange('fabricPreference', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select fabric preference</option>
                    <option value="linen">Premium Linen (Recommended)</option>
                    <option value="cotton">High-Quality Cotton</option>
                    <option value="velvet">Luxe Velvet</option>
                    <option value="canvas">Durable Canvas</option>
                    <option value="discuss">Let's Discuss Options</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Color Scheme Preference</label>
                  <select
                    value={formData.colorScheme}
                    onChange={(e) => handleInputChange('colorScheme', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select color preference</option>
                    <option value="realistic">Realistic Colors</option>
                    <option value="warm-tones">Warm Earth Tones</option>
                    <option value="cool-tones">Cool Blue/Green Tones</option>
                    <option value="neutral">Neutral Palette</option>
                    <option value="custom">Custom Color Scheme</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Design Description & Special Requests</label>
                <textarea
                  rows={4}
                  value={formData.designDescription}
                  onChange={(e) => handleInputChange('designDescription', e.target.value)}
                  placeholder="Tell us about your vision... Any specific details you want emphasized? Colors you love or want to avoid? Is this for a particular room or decor style?"
                  className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                />
              </div>
            </div>
          )}

          {/* Step 4: Final Details */}
          {currentStep === 4 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Final Details</h2>
                <p className="text-slate-600 text-lg">Timeline, budget, and any special considerations</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Preferred Timeline *</label>
                  <select
                    required
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select timeline</option>
                    <option value="rush">Rush Order (2-3 weeks) - Additional fee applies</option>
                    <option value="standard">Standard (4-6 weeks) - Recommended</option>
                    <option value="extended">Extended (6-8 weeks) - For complex designs</option>
                    <option value="flexible">Flexible - No rush needed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Budget Range</label>
                  <select
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                  >
                    <option value="">Select budget range</option>
                    <option value="200-300">$200 - $300</option>
                    <option value="300-500">$300 - $500</option>
                    <option value="500-750">$500 - $750</option>
                    <option value="750-plus">$750+</option>
                    <option value="discuss">Let's discuss pricing</option>
                  </select>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="isGift"
                    checked={formData.isGift}
                    onChange={(e) => handleInputChange('isGift', e.target.checked)}
                    className="w-5 h-5 text-emerald-600 border-amber-300 rounded focus:ring-emerald-500"
                  />
                  <label htmlFor="isGift" className="ml-3 text-amber-800 font-semibold">
                    This is a gift
                  </label>
                </div>
                {formData.isGift && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-amber-700 mb-2">Gift Occasion</label>
                    <input
                      type="text"
                      value={formData.giftOccasion}
                      onChange={(e) => handleInputChange('giftOccasion', e.target.value)}
                      placeholder="e.g., Housewarming, Wedding, Anniversary, Christmas"
                      className="w-full px-4 py-3 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Additional Notes or Questions</label>
                <textarea
                  rows={4}
                  value={formData.specialInstructions}
                  onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                  placeholder="Any other details, questions, or special requests you'd like to share with our artist..."
                  className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                />
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-slate-50 to-stone-100 rounded-2xl p-8 border border-stone-200">
                <h3 className="font-bold text-slate-900 text-xl mb-6 flex items-center">
                  <Award className="h-6 w-6 mr-3 text-emerald-600" />
                  Commission Summary
                </h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Client:</span>
                      <span className="text-slate-900 font-semibold">{formData.firstName} {formData.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">House Style:</span>
                      <span className="text-slate-900">{formData.houseStyle || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Pillow Size:</span>
                      <span className="text-slate-900">{formData.pillowSize || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Quantity:</span>
                      <span className="text-slate-900">{formData.quantity}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Timeline:</span>
                      <span className="text-slate-900">{formData.timeline || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Fabric:</span>
                      <span className="text-slate-900">{formData.fabricPreference || 'To be discussed'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Photos:</span>
                      <span className="text-slate-900">{formData.referenceFiles.length} uploaded</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">Gift:</span>
                      <span className="text-slate-900">{formData.isGift ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-10 border-t border-stone-200">
            <button
              type="button"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                currentStep === 1
                  ? 'text-stone-400 cursor-not-allowed'
                  : 'text-slate-700 bg-stone-100 hover:bg-stone-200 hover:shadow-md'
              }`}
              disabled={currentStep === 1}
            >
              Previous Step
            </button>
            
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={() => {
                  const nextStep = currentStep + 1;
                  setCurrentStep(nextStep);
                  
                  // Track step progression
                  trackEvent('form_step_complete', {
                    form_name: 'commission_order',
                    step_completed: currentStep,
                    step_entered: nextStep
                  });
                }}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Next Step
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  'Submit Commission'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}